import { useContext } from 'react';
import {
  preferenceOptionToBaseTag,
  usePagePreferenceOptions,
} from '@hungryroot/preference-options';

import { type PreferenceOptionKey } from '@hungryroot/rest/api/v2/preference_options/get/get_preference_options';

import { QuizContext } from '../../quiz-context';
import { type QuizTypes, type QuizTagKeys } from '../../quiz-hooks';
import { type QuizPreferenceItem } from '../../segment/quizAnalytics';
import {
  type ControlChildren,
  getSelectedKeys,
  renderTags,
} from '../control-utils';
import { ControlSelect } from '../ControlSelect/ControlSelect';

export type Props<K extends QuizTagKeys> = {
  ariaLabel: string;
  question: K;
  preferenceOptionKey: PreferenceOptionKey;
  children: ControlChildren;
  layoutVariant?:
    | 'one-column'
    | 'two-column'
    | 'one-column-to-two-column'
    | 'three-column';
  max?: number;
  staticList?: boolean;
  defaultValue: [] | undefined;
  tagExclusions?: number[];
  onTrackSelected?: (items: QuizPreferenceItem[]) => void;
};

export function ControlPreferenceOptionSelect<K extends QuizTagKeys>(
  props: Props<K>
) {
  const {
    ariaLabel,
    max,
    question,
    preferenceOptionKey,
    children,
    layoutVariant,
    defaultValue,
    tagExclusions,
    onTrackSelected,
  } = props;

  const tagsForPageDisplay = usePagePreferenceOptions(preferenceOptionKey);

  const { quiz } = useContext(QuizContext);

  if (tagsForPageDisplay === undefined) {
    return null;
  }

  const items = tagsForPageDisplay.map(({ tag_id, name }) => ({
    label: name,
    value: tag_id,
  }));

  const selectedKeys = getSelectedKeys(quiz, question);

  return (
    <ControlSelect
      ariaLabel={ariaLabel}
      question={question}
      defaultValue={defaultValue}
      layoutVariant={layoutVariant}
      max={max}
      items={items}
      selectedKeys={selectedKeys as QuizTypes[]}
      onTrackSelected={onTrackSelected}
    >
      {renderTags(
        question,
        quiz,
        tagsForPageDisplay.map(preferenceOptionToBaseTag),
        children,
        { tagExclusions }
      )}
    </ControlSelect>
  );
}
